// "From public records" under the quote form's property address: what the EPC
// register and HM Land Registry say about it (GET /api/property-facts,
// src/propertyFacts.ts), for information only. If the look-up fails, is slow
// or finds nothing, the box just stays empty: the form never depends on it.

import { escapeHtml } from '../format';

interface Sale { pricePaid: number; date: string; propertyType: string | null; tenure: string | null; newBuild: boolean }
interface Facts {
  epc: { status: string; band: string | null; score: number | null; expiryDate: string | null;
    details: { floorAreaM2: number | null; constructionAgeBand: string | null; builtForm: string | null; propertyType: string | null; habitableRooms: number | null } | null };
  landRegistry: { status: string; sales: Sale[]; indexed: { valueGbp: number; toMonth: string; area: string } | null };
  planning?: { status: string; conservationAreas: string[]; article4: string[]; floodZone: 2 | 3 | null; greenBelt: boolean };
}

const WAIT_MS = 25_000;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthYear = (ym: string) => { const m = /^(\d{4})-(\d{2})/.exec(ym || ''); return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : ''; };
const dmy = (iso: string | null) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ''); return m ? `${m[3]}/${m[2]}/${m[1]}` : ''; };
const pounds = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`;
const SALE_TYPES: Record<string, string> = { detached: 'detached', 'semi-detached': 'semi-detached', terraced: 'terraced', 'flat-maisonette': 'flat or maisonette' };

export function propertyFactsHtml(f: Facts): string {
  const rows: [string, string][] = [];
  const d = f.epc.details;
  if (f.epc.status === 'found') {
    rows.push(['EPC rating', `${f.epc.band ?? '—'}${f.epc.score ? ` (score ${f.epc.score})` : ''}${f.epc.expiryDate ? `, valid until ${dmy(f.epc.expiryDate)}` : ''}`]);
    if (d?.floorAreaM2) rows.push(['Floor area', `${Math.round(d.floorAreaM2)} m² (${Math.round(d.floorAreaM2 * 10.7639).toLocaleString('en-GB')} sq ft)`]);
    const type = [d?.builtForm, d?.propertyType].filter(Boolean).join(' ').toLowerCase();
    if (type) rows.push(['Type', type.charAt(0).toUpperCase() + type.slice(1)]);
    if (d?.constructionAgeBand) rows.push(['Built', d.constructionAgeBand]);
  }
  const last = f.landRegistry.status === 'found' ? f.landRegistry.sales[0] : undefined;
  if (last) {
    const about = [last.tenure, last.propertyType ? SALE_TYPES[last.propertyType] : '', last.newBuild ? 'new build' : ''].filter(Boolean);
    rows.push(['Last sold', `${pounds(last.pricePaid)} in ${monthYear(last.date)}${about.length ? ` (${about.join(', ')})` : ''}`]);
    const ix = f.landRegistry.indexed;
    if (ix) rows.push(['Value today, roughly', `${pounds(ix.valueGbp)}, from the house price index for ${ix.area} to ${monthYear(ix.toMonth)}. An estimate, not a valuation.`]);
  }
  const pl = f.planning;
  if (pl && pl.status === 'found') {
    if (pl.floodZone) rows.push(['Flood zone', `Zone ${pl.floodZone} at the postcode`]);
    if (pl.conservationAreas.length) rows.push(['Conservation area', `The postcode is in ${pl.conservationAreas.join(', ')}`]);
    if (pl.article4.length) rows.push(['Article 4 direction', pl.article4.join(', ')]);
    if (pl.greenBelt) rows.push(['Green belt', 'The postcode is in the green belt']);
  }
  if (!rows.length) return '';
  return `<p class="property-facts-title">From public records</p><dl>${rows.map(([k, v]) => `<div><dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd></div>`).join('')}</dl>
    <p class="hint">From the EPC register, HM Land Registry and the planning register, for information.</p>`;
}

/** Shows the box for an address (empties it first; a slower, older answer never replaces a newer one). */
export function attachPropertyFacts(box: HTMLElement): (address: string) => void {
  let asked = 0;
  return (address: string) => {
    const mine = ++asked;
    box.innerHTML = '';
    box.hidden = true;
    const a = address.trim();
    if (a.length < 8) return;
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), WAIT_MS));
    const answer = fetch(`/api/property-facts?${new URLSearchParams({ address: a })}`)
      .then((r) => (r.ok ? (r.json() as Promise<{ facts?: Facts }>) : null))
      .then((b) => b?.facts ?? null)
      .catch(() => null);
    void Promise.race([answer, timeout]).then((facts) => {
      if (mine !== asked || !facts) return;
      const html = propertyFactsHtml(facts);
      box.innerHTML = html;
      box.hidden = !html;
    });
  };
}
